import { FC, Fragment, ReactNode, useEffect, useMemo, useState, useCallback } from 'react';

import classNames from 'classnames';

import { useAppSelector } from '../../../hooks/redux';
import { selectUser } from '../../../store/auth.slice';

import { ConfirmDialog, Button, CloseButton } from '../../../shared/ui';
import {
  FEATURE_FLAG_TOOL_COLOR_CODE,
  FEATURE_FLAG_TOOL_CLOUDS,
  FEATURE_FLAG_TOOL_GROW_AI,
  FEATURE_FLAG_TOOL_SAND_THERAPY,
} from '../../../constants/featureFlags';
import { useFeatureFlagEnabled } from '../../../hooks/useFeatureFlagEnabled';
import { useTariffToolCheck } from '../../../hooks/useTariffToolCheck';

import { ButtonTool } from './ButtonTool';
import { useMacHasResults } from '../../../hooks/useMacHasResults';
import {
  IMobileToolSheetApi,
  MobileToolCloseFab,
  MobileToolSettingsFab,
  MobileToolSheet,
  MobileToolSheetContext,
} from './mobile-tool-sheet';
import { PsyToolsId, toolNamesMap } from './types';

const confirmableTools = new Set<PsyToolsId>([
  PsyToolsId.MAC_CARDS,
  PsyToolsId.DPDG,
  PsyToolsId.EMOTIONS,
  PsyToolsId.WHEEL,
  PsyToolsId.COLOR_CODE,
  PsyToolsId.CLOUDS,
  PsyToolsId.SAND_THERAPY,
]);

const confirmTexts: Record<PsyToolsId, { name: string; description: string }> = {
  [PsyToolsId.EMOTIONS]: {
    name: '«Детектор эмоций»',
    description: 'Клиент перестанет видеть детектор эмоций.',
  },
  [PsyToolsId.DPDG]: {
    name: '«ДПДГ»',
    description: 'Клиент перестанет видеть упражнение.',
  },
  [PsyToolsId.MAC_CARDS]: {
    name: '«МАК карты»',
    description: 'Клиент перестанет видеть карты.',
  },
  [PsyToolsId.QUESTION]: {
    name: '«Новый вопрос»',
    description: '',
  },
  [PsyToolsId.WHEEL]: {
    name: '«Колесо баланса»',
    description: 'Клиент перестанет видеть колесо баланса.',
  },
  [PsyToolsId.COLOR_CODE]: {
    name: '«Цветовой код»',
    description: 'Клиент перестанет видеть цветовой код.',
  },
  [PsyToolsId.CLOUDS]: {
    name: '«Облака»',
    description: 'Клиент перестанет видеть облака.',
  },
  [PsyToolsId.GROW]: {
    name: '«Ai GROW»',
    description: '',
  },
  [PsyToolsId.SAND_THERAPY]: {
    name: '«Песочная терапия»',
    description: 'Клиент перестанет видеть инструмент.',
  },
};
export interface IToolsPanelProps {
  menu?: PsyToolsId;
  setMenu?: (num: PsyToolsId | 0) => void;
  isGrowOpen?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  users: any[];
  panel?: ReactNode;
  closeHidden?: boolean;
  roomName?: string;
  /** Без фона и скруглений за колонкой иконок (мобильный 1:1: иконки лежат поверх видео) */
  transparentBackground?: boolean;
  /**
   * Мобильный 1:1: колонка иконок видна только пока инструмент не выбран,
   * панель инструмента открывается нижней шторкой (а у запущенного инструмента сворачивается в кнопку «Настройки»).
   */
  mobileSheet?: boolean;
}

const mobileSheetTitles: Partial<Record<PsyToolsId, string>> = {
  [PsyToolsId.QUESTION]: 'Подкинь вопрос',
  [PsyToolsId.DPDG]: 'Видео ДПДГ',
  [PsyToolsId.MAC_CARDS]: 'МАК-карты',
  [PsyToolsId.COLOR_CODE]: 'Цветовой код',
  [PsyToolsId.WHEEL]: 'Колесо жизненного баланса',
  [PsyToolsId.EMOTIONS]: 'Детектор эмоций',
  [PsyToolsId.CLOUDS]: 'Облака',
  [PsyToolsId.SAND_THERAPY]: 'Песочная терапия',
};

/** Инструменты, панель которых хранит состояние (запрос к ИИ, таймер): в шторке её только скрываем, не размонтируем */
const mobileKeepMountedMenus = new Set<PsyToolsId>([PsyToolsId.COLOR_CODE, PsyToolsId.EMOTIONS, PsyToolsId.CLOUDS]);
/** Запущенные инструменты, у которых нет собственной кнопки завершения: всегда показываем ✕ слева сверху */
const mobileCloseFabMenus = new Set<PsyToolsId>([
  PsyToolsId.MAC_CARDS,
  PsyToolsId.COLOR_CODE,
  PsyToolsId.WHEEL,
  PsyToolsId.EMOTIONS,
  PsyToolsId.CLOUDS,
]);

export const ToolsPanel: FC<IToolsPanelProps> = ({
  menu,
  setMenu,
  isGrowOpen = false,
  users,
  panel,
  closeHidden,
  roomName,
  transparentBackground = false,
  mobileSheet = false,
}) => {
  const userData = useAppSelector(selectUser);
  const tariff = useAppSelector((s) => s.tariffSlice);

  const psy_tools = userData?.active_psy_tools;
  const noClientConnected = users.length <= 1;
  const shown_tools = psy_tools;
  const [pendingCloseTool, setPendingCloseTool] = useState<PsyToolsId | null>(null);
  const activeTool = useAppSelector((state) => state.activeToolStateSlice.activeTool);
  const isConfirmCloseOpen = pendingCloseTool !== null;
  const isColorCodeEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_COLOR_CODE);
  const isCloudsEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_CLOUDS);
  const isSandTherapyEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_SAND_THERAPY);
  const isGrowAiEnabled = useFeatureFlagEnabled(FEATURE_FLAG_TOOL_GROW_AI);
  const canUseEmotions = useTariffToolCheck(toolNamesMap[PsyToolsId.EMOTIONS]);
  const canUseDpdg = useTariffToolCheck(toolNamesMap[PsyToolsId.DPDG]);
  const canUseMacCards = useTariffToolCheck(toolNamesMap[PsyToolsId.MAC_CARDS]);
  const canUseQuestion = useTariffToolCheck(toolNamesMap[PsyToolsId.QUESTION]);
  const canUseWheel = useTariffToolCheck(toolNamesMap[PsyToolsId.WHEEL]);
  const canUseColorCode = useTariffToolCheck(toolNamesMap[PsyToolsId.COLOR_CODE]);
  const canUseClouds = useTariffToolCheck(toolNamesMap[PsyToolsId.CLOUDS]);
  const canUseSandTherapy = useTariffToolCheck(toolNamesMap[PsyToolsId.SAND_THERAPY]);
  const canUseToolByName = useMemo(
    () => ({
      [toolNamesMap[PsyToolsId.EMOTIONS]]: canUseEmotions,
      [toolNamesMap[PsyToolsId.DPDG]]: canUseDpdg,
      [toolNamesMap[PsyToolsId.MAC_CARDS]]: canUseMacCards,
      [toolNamesMap[PsyToolsId.QUESTION]]: canUseQuestion,
      [toolNamesMap[PsyToolsId.WHEEL]]: canUseWheel,
      [toolNamesMap[PsyToolsId.COLOR_CODE]]: canUseColorCode,
      [toolNamesMap[PsyToolsId.CLOUDS]]: canUseClouds,
      [toolNamesMap[PsyToolsId.SAND_THERAPY]]: canUseSandTherapy,
    }),
    [
      canUseClouds,
      canUseColorCode,
      canUseDpdg,
      canUseEmotions,
      canUseMacCards,
      canUseQuestion,
      canUseSandTherapy,
      canUseWheel,
    ],
  );

  const onSetMenu = useCallback(
    (num: PsyToolsId | 0) => {
      if (!setMenu) {
        return;
      }

      const nextValue = num === menu ? 0 : num;
      setMenu(nextValue);
    },
    [menu, setMenu],
  );

  useEffect(() => {
    if (pendingCloseTool && activeTool !== pendingCloseTool) {
      setPendingCloseTool(null);
    }
  }, [activeTool, pendingCloseTool]);

  const handlePanelClose = () => {
    if (menu && confirmableTools.has(menu as PsyToolsId) && activeTool === menu) {
      setPendingCloseTool(menu as PsyToolsId);
      return;
    }

    onSetMenu(0);
  };

  const confirmCloseTool = () => {
    onSetMenu(0);
    setPendingCloseTool(null);
  };

  const cancelCloseTool = () => {
    setPendingCloseTool(null);
  };

  const macHasResults = useMacHasResults();
  const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false);
  // Меняется при «новом раунде»: панель инструмента пересоздаётся и сбрасывает своё состояние
  const [mobilePanelKey, setMobilePanelKey] = useState(0);
  const isMobileSheetMode = mobileSheet && Boolean(menu);
  const isMenuToolRunning = Boolean(menu) && activeTool === menu;
  const isMacMenu = menu === PsyToolsId.MAC_CARDS;
  // МАК: когда клиент сделал выбор, карты психолога показываются на весь экран, а не в шторке.
  // Колесо баланса: запущенное колесо всегда на весь экран (шторкой остаётся только запуск)
  const isWheelMenu = menu === PsyToolsId.WHEEL;
  // Песочная терапия: поле клиента на весь экран; завершение (с сохранением в журнал) — кнопка внутри самой панели
  const isSandMenu = menu === PsyToolsId.SAND_THERAPY;
  const isMobileInlinePanel =
    isMobileSheetMode && isMenuToolRunning && ((isMacMenu && macHasResults) || isWheelMenu || isSandMenu);
  const showMobileSheet =
    isMobileSheetMode && !isMobileInlinePanel && Boolean(panel) && (!isMenuToolRunning || mobileSettingsOpen);
  const showMobileSettingsFab =
    isMobileSheetMode && !isMobileInlinePanel && Boolean(panel) && isMenuToolRunning && !mobileSettingsOpen;
  const keepSheetMounted =
    isMobileSheetMode &&
    !isMobileInlinePanel &&
    Boolean(panel) &&
    Boolean(menu) &&
    mobileKeepMountedMenus.has(menu as PsyToolsId);
  // У МАК и цветового кода нет кнопки завершения в панели, поэтому у запущенного инструмента всегда есть ✕
  const showMobileCloseFab = isMobileSheetMode && isMenuToolRunning && mobileCloseFabMenus.has(menu as PsyToolsId);
  const mobileSheetTitle = (menu && mobileSheetTitles[menu]) || '';

  useEffect(() => {
    // Подсказка МАК нужна сразу после запуска; остальные запущенные инструменты стартуют со свёрнутой шторкой
    setMobileSettingsOpen(menu === PsyToolsId.MAC_CARDS && activeTool === PsyToolsId.MAC_CARDS);
  }, [menu, activeTool]);

  const mobileSheetApi: IMobileToolSheetApi = useMemo(
    () => ({
      expand: () => setMobileSettingsOpen(true),
      collapse: () => setMobileSettingsOpen(false),
      restart: () => {
        setMobilePanelKey((prev) => prev + 1);
        if (menu) setMenu?.(menu);
      },
      finish: () => handlePanelClose(),
    }),
    // handlePanelClose пересоздаётся на каждый рендер, но читает актуальные menu/activeTool
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [menu, setMenu, activeTool],
  );

  const confirmData = pendingCloseTool ? confirmTexts[pendingCloseTool] : null;
  const confirmMessage = confirmData
    ? `Вы действительно хотите закрыть инструмент ${confirmData.name}?${confirmData.description ? ` ${confirmData.description}` : ''}`
    : '';

  const iconButtonSet = useMemo(() => {
    return Object.values(PsyToolsId)
      .filter((v): v is PsyToolsId => typeof v === 'number')
      .filter((v) => {
        const tool = toolNamesMap[v];
        if (tool === 'grow_ai' && !isGrowAiEnabled) return false;
        if (tool === 'color_code' && !isColorCodeEnabled) return false;
        if (tool === 'clouds' && !isCloudsEnabled) return false;
        if (tool === 'sand_therapy' && !isSandTherapyEnabled) return false;

        if (canUseToolByName[tool]) {
          return shown_tools?.includes(tool);
        }

        return true; //show if tool disabled by tariff
      })
      .map((toolId, index) => {
        const key = `bt-${index}-${toolId ?? 0}`;

        return (
          <ButtonTool
            key={key}
            selected={toolId === PsyToolsId.GROW ? isGrowOpen : menu === toolId}
            thisMenuId={toolId}
            disabled={noClientConnected}
            onSetMenu={onSetMenu}
          />
        );
      });
  }, [
    menu,
    isGrowOpen,
    noClientConnected,
    onSetMenu,
    shown_tools,
    tariff,
    canUseToolByName,
    isColorCodeEnabled,
    isCloudsEnabled,
    isSandTherapyEnabled,
    isGrowAiEnabled,
  ]);

  return (
    <>
      <div
        className={classNames(
          isMobileSheetMode && !isMobileInlinePanel
            ? 'contents'
            : 'w-full h-full min-h-0 flex flex-row grow gap-0 md:basis-0',
          !isMobileSheetMode &&
            (transparentBackground ? 'bg-transparent' : 'bg-ink-muted sm:rounded-chip md:rounded-card'),
        )}
      >
        {!isMobileSheetMode && (
          <div
            className={classNames('h-full flex flex-col items-center gap-[1rem] justify-start m-[0.5rem]', {
              'w-[48px]': !psy_tools || psy_tools.length <= 0,
            })}
          >
            {iconButtonSet}
          </div>
        )}

        {(showMobileSheet || keepSheetMounted) && (
          <MobileToolSheetContext.Provider value={mobileSheetApi}>
            <MobileToolSheet
              tall={isMacMenu || menu === PsyToolsId.COLOR_CODE || menu === PsyToolsId.CLOUDS}
              hidden={!showMobileSheet}
              title={mobileSheetTitle}
              onClose={isMenuToolRunning ? () => setMobileSettingsOpen(false) : handlePanelClose}
            >
              <Fragment key={mobilePanelKey}>{panel}</Fragment>
            </MobileToolSheet>
          </MobileToolSheetContext.Provider>
        )}
        {showMobileSettingsFab && (
          <MobileToolSettingsFab
            label={isMacMenu ? 'Подсказка' : `Панель: ${mobileSheetTitle}`}
            afterCloseFab={showMobileCloseFab}
            onClick={() => setMobileSettingsOpen(true)}
          />
        )}
        {showMobileCloseFab && (
          <MobileToolCloseFab label={`Завершить: ${mobileSheetTitle}`} onClick={handlePanelClose} />
        )}
        {isMobileInlinePanel && (
          <div className="flex h-full min-h-0 w-full flex-col bg-ink-muted">
            <div className={classNames('min-h-0 flex-1', !isSandMenu && 'overflow-y-auto')}>{panel}</div>
            {!isSandMenu && (
              <div className="flex shrink-0 gap-2 border-t border-white/10 px-4 pb-3 pt-3">
                {/* setMenu(МАК) при открытом МАК закрывает текущий раунд (сброс выбора, сигнал клиенту) и снова открывает выбор колоды */}
                {isMacMenu && (
                  <Button size="md" variant="ghostInverse" fullWidth onClick={() => setMenu?.(PsyToolsId.MAC_CARDS)}>
                    Новый расклад
                  </Button>
                )}
                <Button size="md" fullWidth onClick={handlePanelClose}>
                  Завершить
                </Button>
              </div>
            )}
          </div>
        )}

        {!isMobileSheetMode && panel && (
          <div className="relative flex h-[99%] min-h-0 w-full flex-col items-start grow overflow-hidden bg-ink-muted sm:rounded-chip md:rounded-card ">
            {panel}
            {!closeHidden && <CloseButton tone="solid" className="absolute top-5 right-5" onClick={handlePanelClose} />}
          </div>
        )}
      </div>

      <ConfirmDialog
        isOpen={isConfirmCloseOpen}
        title="Закрытие инструмента"
        message={confirmMessage}
        cancelText="Оставить открытым"
        confirmText="Закрыть инструмент"
        onCancel={cancelCloseTool}
        onConfirm={confirmCloseTool}
      />
    </>
  );
};
